import { Component } from '@nicolas-riciotti/light2'
import isMobile from '../../helpers/isMobile'

/**
 * <core-hover-video data-src="…">
 *
 * Lightweight thumbnail video used by block-models. The poster <img> inside is
 * the baseline — no <video> exists in the DOM until the card is hovered. On
 * hover (while in view) it creates a muted/looping <video>, fades it in over the
 * poster, and plays. The <video> is fully torn down (paused, src cleared,
 * .load(), removed) on mouse-leave AND whenever the card scrolls out of view, so
 * nothing buffers or decodes off-screen. Mobile is image-only — it never loads.
 */
export default class CoreHoverVideo extends Component {

  created() {
    this.$video   = null;
    this.isInView = false;
    this.onEnter  = this.onEnter.bind(this);
    this.onLeave  = this.onLeave.bind(this);
  }

  attached() {
    // Desktop only — mobile keeps the poster image and never loads a video.
    if (isMobile()) return;

    this.src = this.getAttribute('data-src');
    if (!this.src) return;

    // Arm/disarm by viewport. Out of view → tear the video down entirely.
    this._io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        this.isInView = entry.isIntersecting;
        if (!this.isInView) this.teardown();
      }
    }, { rootMargin: '100px 0px' });
    this._io.observe(this);

    this.addEventListener('mouseenter', this.onEnter);
    this.addEventListener('mouseleave', this.onLeave);
  }

  detached() {
    this.removeEventListener('mouseenter', this.onEnter);
    this.removeEventListener('mouseleave', this.onLeave);
    this._io?.disconnect();
    this.teardown();
  }

  onEnter() {
    if (!this.isInView) return;
    this.play();
  }

  onLeave() {
    this.teardown();
  }

  play() {
    if (!this.$video) {
      const video = document.createElement('video');
      video.muted       = true;
      video.loop        = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('aria-hidden', 'true');
      video.preload     = 'auto';
      video.src         = this.src;
      video.className   = 'absolute top-0 left-0 w-full h-full object-cover opacity-0 transition-opacity duration-300 ease-out z-10';
      video.addEventListener('playing', () => { video.style.opacity = '1'; }, { once: true });
      this.appendChild(video);
      this.$video = video;
    }
    const playing = this.$video.play();
    if (playing && playing.catch) playing.catch(() => {});
  }

  teardown() {
    if (!this.$video) return;
    const video = this.$video;
    this.$video = null;
    video.pause();
    video.removeAttribute('src');
    video.load();   // release buffered data / decoder
    video.remove();
  }
}

if (!customElements.get('core-hover-video')) {
  customElements.define('core-hover-video', CoreHoverVideo);
}
